import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtomValue } from 'jotai';

import { Alert, Button } from '@restworld/ui-ds-base';

import { selectedJobApplicationAtom } from '../../../../employer-job-listing.atoms';
import { useJobPositionDetails } from '../../../../hooks/use-employer-job-position-details';
import { BodyWrapper, FooterRow, Header, Title } from './report-candidate-layout';
import { useSelectedReportReason } from './report-reasons-step';

/**
 * Title of the job ad the candidate applied to. It is read from the job position page's own details
 * query (already in the cache: the modal is mounted on that page), so the recap makes no new call.
 */
function useReportedJobTitle() {
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const jobPositionDetails = useJobPositionDetails();

  if (!selectedJobApplication.job_position_id) return undefined;
  return jobPositionDetails?.id === selectedJobApplication.job_position_id ? jobPositionDetails.job_title : undefined;
}

export function ReportConfirmationStep() {
  const { t } = useTranslation('job-positions-page');
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const { reason, draft } = useSelectedReportReason();
  const jobTitle = useReportedJobTitle();

  const detailLabels = useMemo(
    () =>
      (reason?.detail_groups ?? [])
        .flatMap((group) => group.details)
        .filter((detail) => draft.detailKeys.includes(detail.key))
        .map((detail) => detail.label),
    [reason, draft.detailKeys]
  );
  const message = draft.message.trim();
  const isSpontaneous = !selectedJobApplication.job_position_id;

  return (
    <BodyWrapper>
      <Header>
        <Title>{t('report.confirmation_title')}</Title>
      </Header>

      <dl className="flex flex-col gap-4 w-full mb-6">
        <RecapRow label={t('report.recap.reason')} value={reason?.label} />
        {detailLabels.length > 0 ? (
          <RecapRow label={t('report.recap.details')} value={detailLabels.join(', ')} />
        ) : null}
        {message ? <RecapRow label={t('report.recap.notes')} value={message} /> : null}
        {isSpontaneous ? (
          <RecapRow label={t('report.recap.job')} value={t('report.recap.spontaneous')} />
        ) : jobTitle ? (
          <RecapRow label={t('report.recap.job')} value={jobTitle} />
        ) : null}
      </dl>

      <Alert variant="warning" text={t('report.warning')} />
      <p className="mt-4 text-base font-medium text-neutral-700">{t('report.confirm_question')}</p>
    </BodyWrapper>
  );
}

function RecapRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-sm text-neutral-200">{label}</dt>
      <dd className="text-base font-medium text-neutral-700 whitespace-pre-line break-words">{value}</dd>
    </div>
  );
}

export function ReportConfirmationFooter({
  onBack,
  onSubmit,
  isSubmitting,
}: {
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation('job-positions-page');

  return (
    <FooterRow>
      <Button type="button" variant="tertiary" onClick={onBack}>
        {t('report.back')}
      </Button>
      <Button type="button" variant="secondary-danger" loading={isSubmitting} onClick={onSubmit}>
        {t('report.submit')}
      </Button>
    </FooterRow>
  );
}
